import { useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import {
  ChannelAvatar,
  DropdownMenu,
  Flex,
  Sidebar,
  StatusDot,
  Text,
  Tooltip,
  useIsMobileScreenSize,
  useSidebar,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommunityCountsContext } from '~publish/contexts/CommunityCountsContext'
import { useCommunityCounter } from '~publish/contexts/useCommunityCounter'
import { getChannelAdditionalInfo } from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { formatCompactNumber } from '~publish/helpers/numberFormatters'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'

import { useContextAwareChannelUrl } from '../../hooks/useContextAwareChannelUrl'
import { ExpandableSidebarListItem } from '../ExpandableSidebarListItem'
import {
  SecondChannelTourStepOneCoachMark,
  SecondChannelTourStepOneOverlay,
} from '../SecondChannelCelebration'
import { ChannelHoverMenu } from './ChannelHoverMenu'
import { ChannelItemActions } from './ChannelItemActions'
import { ChannelItemSuffix } from './ChannelItemSuffix'
import { ChannelSubNav } from './ChannelSubNav'
import type { MainSidebarChannel } from './useChannels'

import styles from './ChannelItem.module.css'

type ChannelItemProps = {
  channel: MainSidebarChannel
  isExpanded: boolean
  handleNavigation: (channelId: string) => void
  onToggleClick: (channelId: string, isExpanded: boolean) => void
  preventTransitions?: boolean
  dragHandle?: ReactNode
}

export function ChannelItem({
  channel,
  isExpanded,
  handleNavigation,
  onToggleClick,
  preventTransitions = false,
  dragHandle,
}: ChannelItemProps): JSX.Element {
  const additionalInfo = getChannelAdditionalInfo(channel)
  const channelUrl = useContextAwareChannelUrl(channel.id)
  const organizationId = useOrganizationId()
  const {
    getChannelCount,
    getChannelMentionCount,
    mentionsEnabled,
    isChannelSupported,
  } = useCommunityCountsContext()
  const { getChannelTotal, includesMentions } = useCommunityCounter()

  const channelIds = useMemo(() => [channel.id], [channel.id])
  const { counts: postCounts } = usePostCounts({
    organizationId: organizationId ?? undefined,
    channelIds,
  })

  const queueCount = postCounts?.queue ?? 0
  const queueCountLimit = postCounts?.limit
  const communityCount = isChannelSupported(channel)
    ? (getChannelCount(channel.id) ?? 0)
    : 0
  // Effective per-channel count for the sidebar badge/dot: comments + mentions
  // in counter mode, comments only in dot mode. `communityCount` above stays
  // comments-only for the tooltip, which lists comments and mentions separately.
  const communityTotal = isChannelSupported(channel)
    ? getChannelTotal(channel.id)
    : 0
  // `null` when mentions are disabled or the channel doesn't support mentions,
  // which hides the mentions line entirely (vs. 0, which shows "0 unanswered").
  const mentionCount = mentionsEnabled
    ? getChannelMentionCount(channel.id)
    : null

  const tooltipContent = (
    <div className={styles.tooltipContent}>
      <Text size="sm" weight="medium" translate="no">
        {getChannelNameForDisplay(channel)}
      </Text>
      <span className={styles.tooltipCountText}>
        <Text size="sm">
          <Text as="span" size="sm" weight="bold">
            {formatCompactNumber(queueCount, queueCountLimit)}
          </Text>{' '}
          scheduled {queueCount === 1 ? 'post' : 'posts'}
        </Text>
      </span>
      {isChannelSupported(channel) && (
        <span className={styles.tooltipCountText}>
          <Text size="sm">
            <Text as="span" size="sm" weight="bold">
              {communityCount}
            </Text>{' '}
            unanswered {communityCount === 1 ? 'comment' : 'comments'}
          </Text>
        </span>
      )}
      {mentionCount != null && (
        <span className={styles.tooltipCountText}>
          <Text size="sm">
            <Text as="span" size="sm" weight="bold">
              {mentionCount}
            </Text>{' '}
            unanswered {mentionCount === 1 ? 'mention' : 'mentions'}
          </Text>
        </span>
      )}
    </div>
  )

  const isMobile = useIsMobileScreenSize()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  const [hoverMenuOpen, setHoverMenuOpen] = useState(false)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const handlePointerEnter = (): void => {
    clearTimeout(closeTimerRef.current)
    if (!hoverMenuOpen) {
      openTimerRef.current = setTimeout(() => setHoverMenuOpen(true), 200)
    }
  }

  const handlePointerLeave = (): void => {
    clearTimeout(openTimerRef.current)
    closeTimerRef.current = setTimeout(() => setHoverMenuOpen(false), 150)
  }

  useEffect(() => {
    return (): void => {
      clearTimeout(openTimerRef.current)
      clearTimeout(closeTimerRef.current)
    }
  }, [])

  const handleManualToggle = (expanded: boolean): void => {
    // Manual toggle via chevron: only toggle this channel, others stay as-is
    onToggleClick(channel.id, expanded)
  }

  const handleChannelClick = (e: React.MouseEvent): void => {
    if (isMobile) {
      // On mobile, toggle the dropdown instead of navigating
      // so users can see the sub-nav badges
      e.preventDefault()
      onToggleClick(channel.id, !isExpanded)
    } else {
      handleNavigation(channel.id)
    }
  }

  const channelContent = (
    <DropdownMenu
      open={isCollapsed && hoverMenuOpen}
      onOpenChange={setHoverMenuOpen}
      modal={false}
    >
      <div
        className={styles.hoverMenuTrigger}
        onPointerEnter={isCollapsed ? handlePointerEnter : undefined}
        onPointerLeave={isCollapsed ? handlePointerLeave : undefined}
      >
        {/*
          Positioning-only trigger: anchors DropdownMenu.Content to the channel
          row without placing the NavLink inside the trigger's subtree. Radix's
          Enter/Space keyboard handlers are scoped to this hidden span and never
          intercept the NavLink, restoring keyboard navigation.
        */}
        <DropdownMenu.Trigger asChild>
          {/* aria-hidden hides from a11y tree, tabIndex={-1} removes from
              tab order — together they make this a pure positioning anchor */}
          <span
            aria-hidden="true"
            tabIndex={-1}
            className={styles.hoverMenuPositioningAnchor}
          />
        </DropdownMenu.Trigger>
        <ExpandableSidebarListItem
          className={styles.channelItemGroup}
          data-channel-item-group
          expanded={isExpanded}
          onExpandedChange={handleManualToggle}
          preventTransitions={preventTransitions}
        >
          <ExpandableSidebarListItem.Trigger>
            {dragHandle}
            <Tooltip
              content={tooltipContent}
              side="right"
              open={isCollapsed || isMobile ? false : undefined}
            >
              <Sidebar.Button
                as={NavLink}
                to={channelUrl}
                className={styles.channelLink}
                onClick={handleChannelClick}
                aria-keyshortcuts="g Shift+p"
                prefix={
                  <div className={styles.avatarWrapper}>
                    <ChannelAvatar
                      alt=""
                      channel={channel.service}
                      src={channel.avatar}
                      size="xsmall"
                      className={styles.channelAvatar}
                      data-testid="channel-avatar"
                    />
                    {communityTotal > 0 && (
                      <StatusDot className={styles.statusDot} />
                    )}
                  </div>
                }
                suffix={
                  <SecondChannelTourStepOneOverlay channelId={channel.id}>
                    <ChannelItemSuffix
                      channel={channel}
                      isExpanded={isExpanded}
                      queueCount={queueCount}
                      queueCountLimit={queueCountLimit ?? undefined}
                      communityCount={communityTotal}
                      includesMentions={includesMentions}
                    />
                  </SecondChannelTourStepOneOverlay>
                }
              >
                <Flex direction="column" translate="no">
                  <Text className={styles.channelName}>
                    {getChannelNameForDisplay(channel)}
                  </Text>
                  {additionalInfo && (
                    <Text
                      color="subtle"
                      size="sm"
                      className={styles.channelInfo}
                    >
                      {additionalInfo}
                    </Text>
                  )}
                </Flex>
              </Sidebar.Button>
            </Tooltip>
            {/* Sibling of the link, not its suffix, which renders inside the
                <a>. Must stay last to paint above the link it overlays. */}
            <ChannelItemActions channel={channel} />
          </ExpandableSidebarListItem.Trigger>

          <ExpandableSidebarListItem.Content>
            <ChannelSubNav
              channel={channel}
              handleNavigation={handleNavigation}
            />
          </ExpandableSidebarListItem.Content>
        </ExpandableSidebarListItem>
      </div>
      <DropdownMenu.Content
        side="right"
        align="start"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        <ChannelHoverMenu
          channel={channel}
          handleNavigation={handleNavigation}
          queueCount={queueCount}
          queueCountLimit={queueCountLimit ?? undefined}
          communityCount={communityTotal}
          includesMentions={includesMentions}
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  )

  return (
    <SecondChannelTourStepOneCoachMark channelId={channel.id}>
      {channelContent}
    </SecondChannelTourStepOneCoachMark>
  )
}
